import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Link, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { getNotifications, markNotificationRead, type Notification } from '@/api/profile';
import { cn } from '@/lib/utils';

interface NavLink {
  href: string;
  label: string;
}

interface MobileNavProps {
  navLinks: NavLink[];
  onLogout: () => void;
  /** External control of open state */
  open?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (open: boolean) => void;
}

function getNotificationLink(notification: Notification): string | null {
  if (notification.link_url) {
    return notification.link_url;
  }
  if (notification.link_type && notification.link_id) {
    switch (notification.link_type) {
      case 'deal':
        return `/deals/${notification.link_id}`;
      case 'commission':
        return `/commissions/${notification.link_id}`;
      case 'organization':
        return `/admin/organizations/${notification.link_id}`;
      case 'user':
        return `/admin/users/${notification.link_id}`;
      default:
        return null;
    }
  }
  return null;
}

function formatTimeAgo(timestamp: number): string {
  const now = Math.floor(Date.now() / 1000);
  const diff = now - timestamp;

  if (diff < 60) {
    return 'Just now';
  } else if (diff < 3600) {
    const mins = Math.floor(diff / 60);
    return `${mins}m ago`;
  } else if (diff < 86400) {
    const hours = Math.floor(diff / 3600);
    return `${hours}h ago`;
  } else if (diff < 604800) {
    const days = Math.floor(diff / 86400);
    return `${days}d ago`;
  } else {
    return new Date(timestamp * 1000).toLocaleDateString();
  }
}

export function MobileNav({ navLinks, onLogout, open: externalOpen, onOpenChange }: MobileNavProps) {
  const [internalOpen, setInternalOpen] = React.useState(false);

  // Use external state if provided, otherwise use internal state
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;
  const [notifications, setNotifications] = React.useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = React.useState(0);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const { user, hasRole } = useAuthStore();
  const location = useLocation();

  const fetchNotifications = React.useCallback(async () => {
    try {
      const response = await getNotifications();
      setNotifications(response.data.slice(0, 10)); // Show latest 10
      setUnreadCount(response.unread_count);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  }, []);

  // Fetch notifications when menu opens
  React.useEffect(() => {
    if (open) {
      fetchNotifications();
    }
  }, [open, fetchNotifications]);

  async function handleNotificationClick(notification: Notification) {
    if (!notification.is_read) {
      try {
        await markNotificationRead(notification.id);
        setNotifications(prev =>
          prev.map(n => (n.id === notification.id ? { ...n, is_read: true } : n))
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    setOpen(false);
  }

  const isActive = (href: string) => {
    // For admin routes, check prefix match
    if (href.startsWith('/admin/')) {
      return location.pathname.startsWith(href);
    }
    // For regular routes, exact match or prefix match for detail pages
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>
        <button
          className="lg:hidden p-2 text-gray-300 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-bc-teal focus-visible:ring-offset-2 focus-visible:ring-offset-bc-navy rounded-md"
          aria-label="Open menu"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>
      </Dialog.Trigger>

      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[100] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content
          className="fixed inset-y-0 left-0 z-[101] w-[85vw] max-w-[320px] bg-bc-navy shadow-lg data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left duration-200"
          aria-describedby={undefined}
        >
          <div className="flex flex-col h-full">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <Dialog.Title className="text-white font-bold text-lg mb-0">
                Menu
              </Dialog.Title>
              <Dialog.Close asChild>
                <button
                  className="p-2 text-gray-300 hover:text-white rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-bc-teal"
                  aria-label="Close menu"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </Dialog.Close>
            </div>

            {/* User Info */}
            <div className="p-4 border-b border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-bc-teal text-white flex items-center justify-center font-semibold">
                  {user?.first_name?.charAt(0) || 'U'}
                </div>
                <div>
                  <p className="text-white font-medium mb-0">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-gray-400 text-sm mb-0">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Notifications Section */}
            <div className="border-b border-gray-700">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-gray-300 hover:text-white hover:bg-bc-navy-light transition-colors"
              >
                <span className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                  </svg>
                  Notifications
                  {unreadCount > 0 && (
                    <span className="ml-2 flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-bold text-white bg-red-500 rounded-full">
                      {unreadCount > 99 ? '99+' : unreadCount}
                    </span>
                  )}
                </span>
                <svg
                  className={cn(
                    'w-4 h-4 transition-transform',
                    showNotifications && 'rotate-180'
                  )}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {showNotifications && (
                <div className="max-h-64 overflow-y-auto bg-bc-navy-light">
                  {notifications.length === 0 ? (
                    <p className="px-4 py-3 text-sm text-gray-400 text-center">No notifications</p>
                  ) : (
                    <ul className="list-none m-0 p-0">
                      {notifications.map(notification => {
                        const link = getNotificationLink(notification);
                        const content = (
                          <div
                            className={cn(
                              'px-4 py-3 border-b border-gray-700 last:border-b-0',
                              !notification.is_read && 'bg-bc-navy'
                            )}
                          >
                            <p className={cn(
                              'text-sm mb-1',
                              !notification.is_read ? 'text-white font-medium' : 'text-gray-300'
                            )}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-gray-400 line-clamp-2 mb-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-gray-500">
                              {formatTimeAgo(notification.created_at)}
                            </p>
                          </div>
                        );

                        return (
                          <li key={notification.id}>
                            {link ? (
                              <Link
                                to={link}
                                onClick={() => handleNotificationClick(notification)}
                                className="block no-underline"
                              >
                                {content}
                              </Link>
                            ) : (
                              <div
                                onClick={() => handleNotificationClick(notification)}
                                className="cursor-pointer"
                              >
                                {content}
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              )}
            </div>

            {/* Navigation Links - with section headers and active indicator */}
            <nav className="flex-1 overflow-y-auto py-4">
              {/* Main Navigation Section */}
              <div className="px-4 mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Navigation</p>
              </div>
              <ul className="list-none m-0 p-0">
                {navLinks.map((link) => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      onClick={() => setOpen(false)}
                      className={cn(
                        // Enhanced touch target: min-h-[44px] for accessibility
                        'flex items-center px-4 py-3 min-h-[44px] text-sm font-medium transition-all duration-150 no-underline',
                        isActive(link.href)
                          ? 'bg-bc-navy-light text-white border-l-[3px] border-bc-teal'
                          : 'text-gray-300 hover:text-white hover:bg-bc-navy-light border-l-[3px] border-transparent'
                      )}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Account Section */}
            <div className="border-t border-gray-700 p-4">
              <div className="mb-2">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Account</p>
              </div>
              <ul className="list-none m-0 p-0 space-y-1">
                <li>
                  <Link
                    to="/profile"
                    onClick={() => setOpen(false)}
                    className="flex items-center min-h-[44px] px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy-light rounded-md no-underline transition-colors"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Profile
                  </Link>
                </li>
                <li>
                  <Link
                    to="/change-password"
                    onClick={() => setOpen(false)}
                    className="flex items-center min-h-[44px] px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy-light rounded-md no-underline transition-colors"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                    </svg>
                    Change Password
                  </Link>
                </li>
                {hasRole('global_admin') && (
                  <li>
                    <Link
                      to="/admin/settings"
                      onClick={() => setOpen(false)}
                      className="flex items-center min-h-[44px] px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-bc-navy-light rounded-md no-underline transition-colors"
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      System Settings
                    </Link>
                  </li>
                )}
                <li className="pt-2 border-t border-gray-700 mt-2">
                  <button
                    onClick={() => {
                      setOpen(false);
                      onLogout();
                    }}
                    className="flex items-center w-full min-h-[44px] px-4 py-2 text-sm text-red-400 hover:text-red-300 hover:bg-bc-navy-light rounded-md transition-colors"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
